import React, { useEffect, useRef } from 'react';
import { StarIcon, QuoteIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
export function TestimonialsSection() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const testimonials = [{
    name: 'Sarah & Michael',
    text: 'After years of trying, we had almost given up hope. The team at Nura gave us not just medical care, but emotional support that kept us going. Today, our twin boys are the light of our lives.',
    location: 'London, United Kingdom'
  }, {
    name: 'The Murphy Family',
    text: "Working with Nura was the best decision we ever made. The coordination was flawless, and the clinic in Prague exceeded our expectations. We're now blessed with a beautiful daughter.",
    location: 'Dublin, Ireland'
  }, {
    name: 'Emma & Luca',
    text: 'We had tried two other fertility clinics before coming to Nura. The difference in care and expertise was immediately apparent. Their advanced technology and compassionate approach made all the difference.',
    location: 'Rome, Italy'
  }, {
    name: 'The Schmidt Family',
    text: 'The entire journey was seamless from start to finish. Our coordinator was available 24/7 and made sure we felt supported every step of the way. Our son is now 6 months old and thriving.',
    location: 'Berlin, Germany'
  }, {
    name: 'Sophie & Marc',
    text: 'As an international couple, finding the right fertility partner was crucial. Nura provided compassionate care and connected us with an amazing clinic in Barcelona. Forever grateful for our little miracle.',
    location: 'Paris, France'
  }, {
    name: 'The Anderson Family',
    text: 'After multiple failed attempts elsewhere, Nura gave us hope again. The personalized treatment plan and expert care in Warsaw led to our successful pregnancy. Our twins are our miracle.',
    location: 'Toronto, Canada'
  }];
  // Create many duplicates for truly seamless infinite scroll
  const infiniteTestimonials = [...testimonials, ...testimonials, ...testimonials, ...testimonials, ...testimonials, ...testimonials, ...testimonials];
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    // Start at the middle set of testimonials
    const cardWidth = 424; // card width + gap
    const initialScroll = testimonials.length * 3 * cardWidth;
    container.scrollLeft = initialScroll;
    const handleScroll = () => {
      if (isScrollingRef.current) return;
      const container = scrollContainerRef.current;
      if (!container) return;
      const cardWidth = 424;
      const singleSetWidth = testimonials.length * cardWidth;
      const currentScroll = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      // Reset position seamlessly when getting close to edges
      // Use a larger buffer to prevent visible jumps
      if (currentScroll >= maxScroll - cardWidth * 2) {
        isScrollingRef.current = true;
        const offset = currentScroll - maxScroll;
        container.scrollLeft = singleSetWidth * 3 + offset;
        requestAnimationFrame(() => {
          isScrollingRef.current = false;
        });
      } else if (currentScroll <= cardWidth * 2) {
        isScrollingRef.current = true;
        const offset = currentScroll;
        container.scrollLeft = singleSetWidth * 3 + offset;
        requestAnimationFrame(() => {
          isScrollingRef.current = false;
        });
      }
    };
    // Use both scroll and touchmove for better mobile support
    container.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [testimonials.length]);
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 424; // card width + gap
      const newScrollPosition = scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });
    }
  };
  return <section id="testimonials" className="py-20 bg-white overflow-hidden" data-id="element-780">
      <div className="container mx-auto px-4" data-id="element-781">
        <div className="text-center mb-16" data-id="element-782">
          <div className="inline-block bg-blue-50 rounded-full px-4 py-2 mb-4" data-id="element-783">
            <span className="text-blue-700 font-semibold text-sm tracking-wide uppercase" data-id="element-784">
              Testimonials
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 tracking-normal" data-id="element-785">
            Success{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600 italic px-1 py-1 inline-block" data-id="element-786">
              Stories
            </span>
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg font-light leading-relaxed" data-id="element-787">
            Read about the experiences of families who achieved their dreams
            with our help.
          </p>
        </div>
        <div className="relative max-w-7xl mx-auto" data-id="element-788">
          {/* Navigation Buttons */}
          <button onClick={() => scroll('left')} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white border border-slate-200 p-3 rounded-full shadow-lg hover:bg-slate-50 transition-all duration-300 hover:scale-110 hidden md:block" aria-label="Scroll left" data-id="element-789">
            <ChevronLeftIcon className="text-slate-700" size={24} data-id="element-790" />
          </button>
          <button onClick={() => scroll('right')} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white border border-slate-200 p-3 rounded-full shadow-lg hover:bg-slate-50 transition-all duration-300 hover:scale-110 hidden md:block" aria-label="Scroll right" data-id="element-791">
            <ChevronRightIcon className="text-slate-700" size={24} data-id="element-792" />
          </button>
          {/* Scrollable Container */}
          <div ref={scrollContainerRef} className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory pb-4" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }} data-id="element-793">
            {infiniteTestimonials.map((testimonial, index) => <div key={index} className="group bg-white border border-slate-100 rounded-3xl relative hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex-shrink-0 w-[350px] md:w-[400px] snap-start" data-id="element-794">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-t-3xl" data-id="element-795"></div>
                <div className="p-8 flex flex-col h-full" data-id="element-796">
                  <div className="mb-6" data-id="element-797">
                    <h3 className="font-bold text-slate-900 text-lg tracking-normal mb-1" data-id="element-798">
                      {testimonial.name}
                    </h3>
                    <p className="text-slate-500 text-sm font-light mb-4" data-id="element-799">
                      {testimonial.location}
                    </p>
                    <div className="flex" data-id="element-800">
                      {[...Array(5)].map((_, i) => <StarIcon key={i} className="text-amber-400 fill-current" size={16} data-id="element-801" />)}
                    </div>
                  </div>
                  <div className="relative flex-grow" data-id="element-802">
                    <QuoteIcon className="absolute -top-2 -left-2 text-blue-100" size={32} data-id="element-803" />
                    <p className="text-slate-600 leading-relaxed font-light pl-6 pt-4" data-id="element-804">
                      {testimonial.text}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
        <div className="mt-12 text-center" data-id="element-805">
          <a href="/contact" className="inline-block bg-white border-2 border-blue-600 text-blue-600 px-8 py-3 rounded-full hover:bg-blue-50 transition-all duration-300 font-medium tracking-normal hover:scale-105" data-id="element-806">
            Share Your Story
          </a>
        </div>
      </div>
    </section>;
}